/**
 * Unified Patient Onboarding Service
 * Consolidates all patient onboarding API calls and business logic
 */
import axiosInstance from '../../api/axiosInstance';
import { validateAndNormalizePhone } from '../../utils/phoneValidation';

const API_BASE = '/api/v1/patient';

class UnifiedPatientOnboardingService {
  constructor() {
    this.cache = new Map();
    this.retryCount = 0;
    this.maxRetries = 3;
  }

  /**
   * Get onboarding state and progress
   */
  async getOnboardingState() {
    try {
      const response = await axiosInstance.get(`${API_BASE}/onboarding/state/`);
      const state = response.data;
      
      // Cache the state
      this.cache.set('onboardingState', state);
      
      return {
        ...state,
        progress: this.calculateProgress(state),
        nextSteps: this.getNextSteps(state),
        estimatedTime: this.getEstimatedTime(state)
      };
    } catch (error) {
      console.error('Error fetching onboarding state:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get patient profile data
   */
  async getProfile() {
    try {
      const response = await axiosInstance.get(`${API_BASE}/profile/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Save patient profile data
   */
  async saveProfile(profileData) {
    try {
      // Validate and normalize phone number
      if (profileData.phoneE164) {
        const phoneValidation = validateAndNormalizePhone(profileData.phoneE164);
        if (!phoneValidation.isValid) {
          throw new Error(phoneValidation.error);
        }
        profileData.phoneE164 = phoneValidation.normalizedNumber;
      }

      // Validate profile data
      const validation = this.validateProfile(profileData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await axiosInstance.post(`${API_BASE}/profile/`, profileData);
      
      // Update cache
      this.cache.set('profile', response.data);
      
      return response.data;
    } catch (error) {
      console.error('Error saving profile:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get step data for a specific onboarding step
   */
  async getStepData(stepId) {
    try {
      const response = await axiosInstance.get(`${API_BASE}/onboarding/step/${stepId}/`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching step data for ${stepId}:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Save step data for a specific onboarding step
   */
  async saveStepData(stepId, data) {
    try {
      // Validate step data
      const validation = this.validateStepData(stepId, data);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      const response = await axiosInstance.post(`${API_BASE}/onboarding/step/${stepId}/`, data);
      
      // Update cache
      this.cache.set(`step_${stepId}`, response.data);
      
      return response.data;
    } catch (error) {
      console.error(`Error saving step data for ${stepId}:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Mark a section as complete
   */
  async completeSection(sectionId) {
    try {
      const response = await axiosInstance.post(`${API_BASE}/onboarding/complete-section/`, {
        section: sectionId
      });
      
      // Update cache
      const state = this.cache.get('onboardingState');
      if (state) {
        state.completed_sections = [...(state.completed_sections || []), sectionId];
        this.cache.set('onboardingState', state);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error completing section ${sectionId}:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Skip a section
   */
  async skipSection(sectionId) {
    try {
      const response = await axiosInstance.post(`${API_BASE}/onboarding/skip-section/`, {
        section: sectionId
      });
      
      // Update cache
      const state = this.cache.get('onboardingState');
      if (state) {
        state.skipped_sections = [...(state.skipped_sections || []), sectionId];
        this.cache.set('onboardingState', state);
      }
      
      return response.data;
    } catch (error) {
      console.error(`Error skipping section ${sectionId}:`, error);
      throw this.handleError(error);
    }
  }

  /**
   * Complete onboarding
   */
  async completeOnboarding() {
    try {
      const response = await axiosInstance.post(`${API_BASE}/onboarding/complete/`);
      
      // Clear cache
      this.cache.clear();
      
      return response.data;
    } catch (error) {
      console.error('Error completing onboarding:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get intake form availability
   */
  async getIntakeAvailability() {
    try {
      const response = await axiosInstance.get(`${API_BASE}/onboarding/intake/availability/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching intake availability:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Create trial state
   */
  async createTrialState() {
    try {
      const response = await axiosInstance.post(`${API_BASE}/onboarding/create-trial/`);
      return response.data;
    } catch (error) {
      console.error('Error creating trial state:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Validate profile data
   */
  validateProfile(profileData) {
    const errors = [];
    
    // Required fields
    if (!profileData.fullName || profileData.fullName.trim() === '') {
      errors.push('Full name is required');
    }
    
    if (!profileData.phoneE164 || profileData.phoneE164.trim() === '') {
      errors.push('Phone number is required');
    }
    
    // Optional field validations
    if (profileData.email && !this.isValidEmail(profileData.email)) {
      errors.push('Please enter a valid email address');
    }
    
    if (profileData.postalCode && !this.isValidPostalCode(profileData.postalCode)) {
      errors.push('Please enter a valid postal code');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate step data
   */
  validateStepData(stepId, data) {
    const errors = [];
    
    switch (stepId) {
      case 'profile':
        return this.validateProfile(data);
      
      case 'biometrics':
        if (!data.height || data.height <= 0) {
          errors.push('Height is required');
        }
        if (!data.weight || data.weight <= 0) {
          errors.push('Weight is required');
        }
        break;
      
      case 'documents':
        // Document validation is optional
        break;
      
      case 'intake':
        if (!data.medicalHistory || data.medicalHistory.trim() === '') {
          errors.push('Medical history is required');
        }
        break;
      
      default:
        // Generic validation
        break;
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Calculate onboarding progress
   */
  calculateProgress(state) {
    const totalSteps = 5; // profile, biometrics, documents, intake, next-steps
    const completedSteps = state.completed_sections?.length || 0;
    return Math.round((completedSteps / totalSteps) * 100);
  }

  /**
   * Get next steps
   */
  getNextSteps(state) {
    const completedSections = state.completed_sections || [];
    const nextSteps = [];
    
    const steps = ['profile', 'biometrics', 'documents', 'intake', 'next-steps'];
    
    for (const step of steps) {
      if (!completedSections.includes(step)) {
        nextSteps.push(step);
      }
    }
    
    return nextSteps;
  }

  /**
   * Get estimated time remaining
   */
  getEstimatedTime(state) {
    const completedSections = state.completed_sections || [];
    const timeEstimates = {
      profile: 2,
      biometrics: 3,
      documents: 5,
      intake: 10,
      'next-steps': 2
    };
    
    let totalTime = 0;
    for (const [step, time] of Object.entries(timeEstimates)) {
      if (!completedSections.includes(step)) {
        totalTime += time;
      }
    }
    
    return totalTime;
  }

  /**
   * Helper methods
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPostalCode(postalCode) {
    return postalCode.length >= 3 && postalCode.length <= 10;
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          return new Error(data.detail || 'Invalid request data');
        case 401:
          return new Error('Authentication required');
        case 403:
          return new Error('Access denied');
        case 404:
          return new Error('Resource not found');
        case 500:
          return new Error('Server error. Please try again later');
        default:
          return new Error(data.detail || 'An error occurred');
      }
    } else if (error.request) {
      // Network error
      return new Error('Network error. Please check your connection');
    } else {
      // Other error
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Get cached data
   */
  getCachedData(key) {
    return this.cache.get(key);
  }

  /**
   * Set cached data
   */
  setCachedData(key, data) {
    this.cache.set(key, data);
  }
}

// Create singleton instance
const unifiedPatientOnboardingService = new UnifiedPatientOnboardingService();

export default unifiedPatientOnboardingService;